import _ from 'lodash';
import React from 'react';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  ListItemText,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  errorUtil,
  modalUtil
} from 'iron-forge-react-modules';

import { Cancel } from '@mui/icons-material';
import Big from 'big.js';
import { LabelledText } from 'modules';
import * as BulkUpdateListedStoresModal from './BulkUpdateListedStoresModal/BulkUpdateListedStoresModal';
import * as dataService from './dataService';
import * as UpdateItemPriceModal from './UpdateItemPriceModal/UpdateItemPriceModal';
import * as UpdateListingPriceModal from './UpdateListingPriceModal/UpdateListingPriceModal';



export interface IProps {
  itemId: string;
  onClose: () => void;
}

export const FC: React.FC<IProps> = ({ itemId, onClose }) => {
  const queryTuple = dataService.useQuery(itemId);
  const [
    toggleListingManagementStrategiesForItem
  ] = dataService.useToggleListingManagementStrategiesForItem(itemId);
  const [
    deleteListingManagementStrategy
  ] = dataService.useDeleteListingManagementStrategy();

  const {
    modalType2IsOpen,
    modalType2TargetId,
    setModalIsOpen
  } = modalUtil.useModalTrackers([
    'updateStorePrice',
    'updateWholesalePrice',
    'bulkUpdateListedStores',
    'updateListingPrice'
  ]);




  const handleChangeIsActive = React.useCallback(
    async (isActive: boolean) => {
      if (window.confirm(`Are you sure you want to globally ${isActive ? "enable" : "hide"} this item?`)) {
        await errorUtil.errorWrap(async () => {
          await toggleListingManagementStrategiesForItem(isActive);
        }, {});
      }
    },
    [toggleListingManagementStrategiesForItem]
  );



  const handleClickDelete = React.useCallback(
    async (id: string) => {
      if (window.confirm("Are you sure you want to stop managing the item on this store?")) {
        await errorUtil.errorWrap(async () => {
          await deleteListingManagementStrategy(id);
        });
      }
    },
    [deleteListingManagementStrategy]
  );





  const elSkeleton = <Skeleton sx={{ minWidth: 100 }} />;
  const loading = queryTuple.loading || !queryTuple.data.sku;
  return (
    <>
      <Drawer
        open
        anchor="right"
        onClose={onClose}>
        <Box px={2}
          minWidth={'35vw'}>
          <Typography variant="h1">
            Item Detail
          </Typography>

          <Divider />

          <Typography variant="h2">
            ERP Information
          </Typography>

          <LabelledText.FC
            title="SKU"
            text={loading ? elSkeleton : queryTuple.data.sku}
            direction="horizontal" />

          <LabelledText.FC
            title="Name"
            text={loading ? elSkeleton : queryTuple.data.name}
            direction="horizontal" />

          <LabelledText.FC
            title="Description"
            text={loading ? elSkeleton : queryTuple.data.description}
            direction="horizontal" />

          <LabelledText.FC
            title="Weight"
            text={loading ? elSkeleton : queryTuple.data.weightValue + ' ' + queryTuple.data.weightUnit}
            direction="horizontal" />

          <LabelledText.FC
            title="Device Type"
            text={loading ? elSkeleton : queryTuple.data.deviceType}
            direction="horizontal" />

          <Typography variant="h2">
            Default Values
          </Typography>

          {queryTuple.loading ? (
            <Skeleton sx={{ maxWidth: '100px' }} />
          ) : (
            <FormControlLabel
              label="Active"
              control={
                <Checkbox
                  checked={queryTuple.data.isActive}
                  onChange={async ev => handleChangeIsActive(ev.target.checked)} />
              } />
          )}

          <LabelledText.FC
            title="Store Price"
            text={loading ? elSkeleton : (
              <Button
                variant="outlined"
                onClick={() => setModalIsOpen('updateStorePrice', true)}>
                {queryTuple.data.defaultPriceDollars.toFixed(2)}
              </Button>
            )}
            direction="horizontal" />

          <LabelledText.FC
            title="Wholesale Price"
            text={loading ? elSkeleton : (
              <Button
                variant="outlined"
                onClick={() => setModalIsOpen('updateWholesalePrice', true)}>
                {queryTuple.data.defaultWholesalePriceDollars.toFixed(2)}
              </Button>
            )}
            direction="horizontal" />

          <LabelledText.FC
            title="Cost"
            direction="horizontal"
            text={loading ? elSkeleton : (
              <Button
                variant="outlined"
                disabled>
                {queryTuple.data.costDollars.toFixed(2)}
              </Button>
            )} />

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            mt={2}
          >
            <Typography variant="h1">
              Stores
            </Typography>

            <Button onClick={() => setModalIsOpen('bulkUpdateListedStores', true)}>
              Bulk Update
            </Button>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />

                <TableCell>Store Name</TableCell>

                <TableCell>Listing Price</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {queryTuple.loading && _.times(5).map(i => (
                <TableRow key={i}>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              ))}
              {!queryTuple.loading && queryTuple.data.managementStrategies.map(strategy => (
                <TableRow key={strategy.storeId}>
                  <TableCell>
                    <IconButton onClick={_.partial(handleClickDelete, strategy.id)}>
                      <Cancel />
                    </IconButton>
                  </TableCell>

                  <TableCell>
                    <ListItemText
                      secondaryTypographyProps={{ sx: { fontSize: 12 } }}
                      primary={strategy.storeName}
                      secondary={strategy.isInventoryManagementDisabled ? "Inventory management disabled" : undefined} />
                  </TableCell>

                  <TableCell>
                    <Button onClick={() => setModalIsOpen('updateListingPrice', strategy.id)}>
                      $ {strategy.priceDollars.toFixed(2)}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>


          <Stack
            direction="row"
            justifyContent="space-between"
            mt={2}
          >
            <Typography variant="h1">
              Locations
            </Typography>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>NS Location</TableCell>
                <TableCell>Quantity</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {queryTuple.loading && _.times(5).map(i => (
                <TableRow key={i}>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              ))}
              {!queryTuple.loading && queryTuple.data.quantitiesOnLocations.map((location, index) => (
                <TableRow key={location.baselineId}>
                  <TableCell>
                    <ListItemText
                      secondaryTypographyProps={{ sx: { fontSize: 12 } }}
                      primary={index + 1}
                    />
                  </TableCell>

                  <TableCell>
                    <ListItemText
                      secondaryTypographyProps={{ sx: { fontSize: 12 } }}
                      primary={location.locationName}
                    />
                  </TableCell>

                  <TableCell>
                    <ListItemText
                      secondaryTypographyProps={{ sx: { fontSize: 12 } }}
                      primary={location.quantity}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

        </Box>
      </Drawer>

      {modalType2IsOpen.updateStorePrice && (
        <UpdateItemPriceModal.FC
          value={queryTuple.data.defaultPriceDollars}
          variant="listingPrice"
          itemId={itemId}
          onClose={() => setModalIsOpen('updateStorePrice', false)} />
      )}

      {modalType2IsOpen.updateWholesalePrice && (
        <UpdateItemPriceModal.FC
          value={queryTuple.data.defaultWholesalePriceDollars}
          variant="wholesalePrice"
          itemId={itemId}
          onClose={() => setModalIsOpen('updateWholesalePrice', false)} />
      )}

      {modalType2IsOpen.bulkUpdateListedStores && (
        <BulkUpdateListedStoresModal.FC
          itemId={itemId}
          onClose={() => setModalIsOpen('bulkUpdateListedStores', false)} />
      )}

      {modalType2TargetId.updateListingPrice && (
        <UpdateListingPriceModal.FC
          listingManagementStrategyId={modalType2TargetId.updateListingPrice}
          value={new Big(queryTuple.data.managementStrategies.find(listing => listing.id === modalType2TargetId.updateListingPrice)?.priceDollars ?? queryTuple.data.defaultPriceDollars)}
          onClose={() => setModalIsOpen('updateListingPrice', false)} />
      )}
    </>
  );
};
